import React from "react";
import { Image as KonvaImage, Text, Rect, Line, Group } from "react-konva";
import Konva from "konva";
import { EditorObject, EditorTool } from "../types/editorTypes";

// ハンドラーの型定義
export interface ObjectHandlers {
  setSelectedId: (id: string) => void;
  setObjects: React.Dispatch<React.SetStateAction<EditorObject[]>>;
  handleTextDoubleClick: (obj: EditorObject, e?: Konva.KonvaEventObject<any>) => void;
  handleRectDoubleClick: (obj: EditorObject, e?: Konva.KonvaEventObject<any>) => void;
  handleEditingClick: (obj: EditorObject, e: Konva.KonvaEventObject<any>) => void;
  handleDragEnd: (id: string, e: Konva.KonvaEventObject<DragEvent>) => void;
  editingTextId: string | null;
  editingTextValue: string;
  cursorVisible: boolean;
  cursorPosition: number;
  objects: EditorObject[];
  loadedImages: { [key: string]: HTMLImageElement };
  refreshTransformer?: () => void;
}

// 描画プレビュー用のProps
export interface DrawingPreviewProps {
  isDrawing: boolean;
  currentPoints: number[];
  currentTool: EditorTool;
  markerColor: string;
  markerStrokeWidth: number;
  rectFillEnabled: boolean;
  rectFillColor: string;
  rectStrokeEnabled: boolean;
  rectStrokeColor: string;
}

/**
 * テキストの視覚的な行数を計算する
 */
const getVisualLineCount = (
  text: string,
  fontSize: number,
  fontFamily: string,
  fontStyle: string,
  textWidth: number,
): number => {
  if (!text) return 1;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 1;

  const fontWeight = fontStyle === 'bold' ? 'bold ' : fontStyle === 'italic' ? 'italic ' : '';
  ctx.font = `${fontWeight}${fontSize}px ${fontFamily}`;

  let lineCount = 0;
  const paragraphs = text.split('\n');

  for (const para of paragraphs) {
    if (para.length === 0) {
      lineCount++;
    } else {
      let lineStart = 0;
      while (lineStart < para.length) {
        let lineEnd = lineStart + 1;
        while (lineEnd <= para.length) {
          if (ctx.measureText(para.slice(lineStart, lineEnd)).width > textWidth && lineEnd - lineStart > 1) {
            lineEnd--;
            break;
          }
          lineEnd++;
        }
        lineEnd = Math.min(lineEnd, para.length);
        lineCount++;
        lineStart = lineEnd;
      }
    }
  }

  return Math.max(1, lineCount);
};

/**
 * verticalAlignに基づいてテキストのyオフセットを計算する
 * テキストがはみ出す場合は0を返す（top配置と同じ）
 */
const getVerticalAlignOffset = (
  text: string,
  fontSize: number,
  fontFamily: string,
  fontStyle: string,
  textWidth: number,
  containerHeight: number,
  padding: number,
  verticalAlign: string,
): number => {
  if (verticalAlign === 'top') return 0;

  const lineCount = getVisualLineCount(text, fontSize, fontFamily, fontStyle, textWidth);
  const textHeight = lineCount * fontSize;
  const availableHeight = containerHeight - padding * 2;
  const freeSpace = availableHeight - textHeight;

  if (freeSpace <= 0) return 0; // はみ出す場合はtop

  if (verticalAlign === 'middle') {
    return freeSpace / 2;
  } else if (verticalAlign === 'bottom') {
    return freeSpace;
  }
  return 0;
};

/**
 * カーソルのピクセル位置を計算する
 */
const getCursorPixelPosition = (
  text: string,
  cursorPos: number,
  fontSize: number,
  fontFamily: string,
  fontStyle: string,
  textWidth: number,
  textAlign: string = 'left',
): { x: number; y: number } => {
  if (!text && cursorPos === 0) return { x: 0, y: 0 };

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return { x: 0, y: 0 };

  const fontWeight = fontStyle === 'bold' ? 'bold ' : fontStyle === 'italic' ? 'italic ' : '';
  ctx.font = `${fontWeight}${fontSize}px ${fontFamily}`;

  const lineHeight = fontSize;

  // テキストを視覚的な行に分割
  const visualLines: { text: string; startIndex: number }[] = [];
  const paragraphs = text.split('\n');
  let globalIndex = 0;

  for (const para of paragraphs) {
    if (para.length === 0) {
      visualLines.push({ text: '', startIndex: globalIndex });
    } else {
      let lineStart = 0;
      while (lineStart < para.length) {
        let lineEnd = lineStart + 1;
        while (lineEnd <= para.length) {
          if (ctx.measureText(para.slice(lineStart, lineEnd)).width > textWidth && lineEnd - lineStart > 1) {
            lineEnd--;
            break;
          }
          lineEnd++;
        }
        lineEnd = Math.min(lineEnd, para.length);
        visualLines.push({
          text: para.slice(lineStart, lineEnd),
          startIndex: globalIndex + lineStart,
        });
        lineStart = lineEnd;
      }
    }
    globalIndex += para.length + 1;
  }

  if (visualLines.length === 0) return { x: 0, y: 0 };

  // カーソルがどの行にいるか特定
  let lineIdx = 0;
  for (let i = 0; i < visualLines.length; i++) {
    const line = visualLines[i];
    const lineEnd = line.startIndex + line.text.length;
    if (cursorPos >= line.startIndex && cursorPos <= lineEnd) {
      lineIdx = i;
      break;
    }
  }

  const line = visualLines[lineIdx];
  const posInLine = cursorPos - line.startIndex;
  let x = ctx.measureText(line.text.slice(0, posInLine)).width;

  // テキスト配置によるオフセット
  if (textAlign === 'center') {
    const lineWidth = ctx.measureText(line.text).width;
    x += (textWidth - lineWidth) / 2;
  } else if (textAlign === 'right') {
    const lineWidth = ctx.measureText(line.text).width;
    x += textWidth - lineWidth;
  }

  const y = lineIdx * lineHeight;
  return { x, y };
};

/**
 * テキストオブジェクトのレンダリング
 */
export const renderTextObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode => {
  const {
    setSelectedId,
    setObjects,
    handleTextDoubleClick,
    handleEditingClick,
    handleDragEnd,
    editingTextId,
    editingTextValue,
    cursorVisible,
    cursorPosition,
    objects,
    refreshTransformer,
  } = handlers;

  const isEditing = obj.id === editingTextId;

  const textPad = 4;
  const textWidth = (obj.width || 150) - textPad * 2;
  const displayText = isEditing ? editingTextValue : (obj.text || '');
  const fSize = obj.fontSize || 14;
  const fFamily = obj.fontFamily || 'sans-serif';
  const fStyle = obj.fontStyle || 'normal';

  // verticalAlignのyオフセットを手動計算（heightなしでも効くように）
  const vAlignOffset = getVerticalAlignOffset(
    displayText, fSize, fFamily, fStyle, textWidth,
    obj.height || 24, textPad, obj.verticalAlign || 'top',
  );

  // カーソルのピクセル位置を計算
  let cursorPixelPos: { x: number; y: number } | null = null;
  if (isEditing) {
    cursorPixelPos = getCursorPixelPosition(
      editingTextValue, cursorPosition, fSize, fFamily, fStyle,
      textWidth, obj.textAlign || 'left',
    );
  }

  return (
    <Group
      key={obj.id}
      id={obj.id}
      x={obj.x}
      y={obj.y}
      rotation={obj.rotation || 0}
      draggable
      ref={(node) => {
        // TransformerがRectの範囲のみを選択枠として使うようにオーバーライド
        if (node) {
          const rect = node.findOne('Rect');
          if (rect) {
            node.getClientRect = (config?: any) => {
              return rect.getClientRect(config);
            };
          }
        }
      }}
      onClick={(e) => { isEditing ? handleEditingClick(obj, e) : setSelectedId(obj.id); }}
      onTap={(e) => { isEditing ? handleEditingClick(obj, e) : setSelectedId(obj.id); }}
      onDblClick={(e) => handleTextDoubleClick(obj, e)}
      onDblTap={(e) => handleTextDoubleClick(obj, e)}
      onDragEnd={(e) => handleDragEnd(obj.id, e)}
      onTransform={(e) => {
        // リサイズ中にテキストサイズを固定する（逆スケールを適用）
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const text = node.findOne('Text');
        if (text) {
          text.scaleX(1 / scaleX);
          text.scaleY(1 / scaleY);
          text.x(textPad / scaleX);
          text.y(textPad / scaleY);
          text.width((obj.width || 150) * scaleX - textPad * 2);
        }
      }}
      onTransformEnd={(e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const newWidth = Math.max(50, (obj.width || 150) * scaleX);
        const newHeight = Math.max(20, (obj.height || 24) * scaleY);

        // スケールをリセット
        node.scaleX(1);
        node.scaleY(1);

        // Group内の子要素のサイズ・スケールを直接更新
        node.find('Rect').forEach((rect: any) => {
          rect.width(newWidth);
          rect.height(newHeight);
        });
        const text = node.findOne('Text');
        if (text) {
          text.scaleX(1);
          text.scaleY(1);
          text.x(textPad);
          text.y(textPad);
          text.width(newWidth - textPad * 2);
          text.fontSize(obj.fontSize);
        }

        setObjects(objects.map(o => {
          if (o.id === obj.id) {
            return {
              ...o,
              x: node.x(),
              y: node.y(),
              width: newWidth,
              height: newHeight,
              rotation: node.rotation(),
            };
          }
          return o;
        }));

        // Transformerを更新（状態更新後に実行）
        setTimeout(() => refreshTransformer?.(), 0);
      }}
    >
      {/* サイズ定義用の透明なRect（常に表示、Transformerがサイズを認識するため必要） */}
      <Rect
        x={0}
        y={0}
        width={obj.width || 150}
        height={obj.height || 24}
        fill={obj.backgroundColor && obj.backgroundColor !== 'transparent' ? obj.backgroundColor : 'transparent'}
        stroke={obj.stroke}
        strokeWidth={obj.strokeWidth}
      />
      {/* テキスト（パディング付き、heightなしではみ出し可能、verticalAlignは手動計算） */}
      <Text
        x={textPad}
        y={textPad + vAlignOffset}
        text={displayText}
        fontSize={fSize}
        fontFamily={fFamily}
        fontStyle={fStyle}
        align={obj.textAlign || 'left'}
        fill={obj.textColor}
        width={textWidth}
        wrap="char"
        listening={false}
      />
      {/* カーソル（別要素として描画、パディング+verticalAlignオフセット） */}
      {isEditing && cursorVisible && cursorPixelPos && (
        <Rect
          x={textPad + cursorPixelPos.x}
          y={textPad + vAlignOffset + cursorPixelPos.y}
          width={1}
          height={fSize}
          fill={obj.textColor || '#000000'}
          listening={false}
        />
      )}
    </Group>
  );
};

/**
 * 四角形オブジェクトのレンダリング
 */
export const renderRectObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode => {
  const {
    setSelectedId,
    setObjects,
    handleRectDoubleClick,
    handleEditingClick,
    handleDragEnd,
    editingTextId,
    editingTextValue,
    cursorVisible,
    cursorPosition,
    objects,
    refreshTransformer,
  } = handlers;

  const isEditing = obj.id === editingTextId;

  const rectPad = 4;
  const rectTextWidth = (obj.width || 100) - rectPad * 2;
  const rectDisplayText = isEditing ? editingTextValue : (obj.text || '');
  const rectFSize = obj.fontSize || 14;
  const rectFFamily = obj.fontFamily || 'sans-serif';
  const rectFStyle = obj.fontStyle || 'normal';

  // verticalAlignのyオフセットを手動計算
  const rectVAlignOffset = getVerticalAlignOffset(
    rectDisplayText, rectFSize, rectFFamily, rectFStyle, rectTextWidth,
    obj.height || 50, rectPad, obj.verticalAlign || 'top',
  );

  // カーソルのピクセル位置を計算
  let cursorPixelPos: { x: number; y: number } | null = null;
  if (isEditing) {
    cursorPixelPos = getCursorPixelPosition(
      editingTextValue, cursorPosition, rectFSize, rectFFamily, rectFStyle,
      rectTextWidth, obj.textAlign || 'left',
    );
  }

  return (
    <Group
      key={obj.id}
      id={obj.id}
      x={obj.x}
      y={obj.y}
      rotation={obj.rotation || 0}
      draggable
      ref={(node) => {
        // TransformerがRectの範囲のみを選択枠として使うようにオーバーライド
        if (node) {
          const rect = node.findOne('Rect');
          if (rect) {
            node.getClientRect = (config?: any) => {
              return rect.getClientRect(config);
            };
          }
        }
      }}
      onClick={(e) => { isEditing ? handleEditingClick(obj, e) : setSelectedId(obj.id); }}
      onTap={(e) => { isEditing ? handleEditingClick(obj, e) : setSelectedId(obj.id); }}
      onDblClick={(e) => handleRectDoubleClick(obj, e)}
      onDblTap={(e) => handleRectDoubleClick(obj, e)}
      onDragEnd={(e) => handleDragEnd(obj.id, e)}
      onTransform={(e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const text = node.findOne('Text');
        if (text) {
          text.scaleX(1 / scaleX);
          text.scaleY(1 / scaleY);
          text.x(4 / scaleX);
          text.y(4 / scaleY);
          text.width((obj.width || 100) * scaleX - 8);
        }
      }}
      onTransformEnd={(e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const newWidth = Math.max(5, (obj.width || 0) * scaleX);
        const newHeight = Math.max(5, (obj.height || 0) * scaleY);

        // スケールをリセット
        node.scaleX(1);
        node.scaleY(1);

        // Group内の子要素のサイズ・スケールを直接更新
        const rect = node.findOne('Rect');
        if (rect) {
          rect.width(newWidth);
          rect.height(newHeight);
        }
        const text = node.findOne('Text');
        if (text) {
          text.scaleX(1);
          text.scaleY(1);
          text.x(4);
          text.y(4);
          text.width(newWidth - 8);
          if (obj.fontSize) text.fontSize(obj.fontSize);
        }

        setObjects(objects.map(o => {
          if (o.id === obj.id) {
            return {
              ...o,
              x: node.x(),
              y: node.y(),
              width: newWidth,
              height: newHeight,
              rotation: node.rotation(),
            };
          }
          return o;
        }));

        // Transformerを更新（状態更新後に実行）
        setTimeout(() => refreshTransformer?.(), 0);
      }}
    >
      <Rect
        x={0}
        y={0}
        width={obj.width}
        height={obj.height}
        fill={obj.backgroundColor}
        stroke={obj.stroke}
        strokeWidth={obj.strokeWidth}
      />
      {(isEditing || obj.text) && (
        <Text
          x={rectPad}
          y={rectPad + rectVAlignOffset}
          text={rectDisplayText}
          fontSize={rectFSize}
          fontFamily={rectFFamily}
          fontStyle={rectFStyle}
          align={obj.textAlign || 'left'}
          fill={obj.textColor || '#000000'}
          width={rectTextWidth}
          wrap="char"
          listening={false}
        />
      )}
      {/* カーソル（別要素として描画、パディング+verticalAlignオフセット） */}
      {isEditing && cursorVisible && cursorPixelPos && (
        <Rect
          x={rectPad + cursorPixelPos.x}
          y={rectPad + rectVAlignOffset + cursorPixelPos.y}
          width={1}
          height={rectFSize}
          fill={obj.textColor || '#000000'}
          listening={false}
        />
      )}
    </Group>
  );
};

/**
 * 線/マーカーオブジェクトのレンダリング
 */
export const renderLineObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode => {
  const { setSelectedId, setObjects, handleDragEnd, objects } = handlers;

  return (
    <Line
      key={obj.id}
      id={obj.id}
      points={obj.points}
      stroke={obj.stroke}
      strokeWidth={obj.strokeWidth}
      lineCap={obj.type === 'marker' ? 'round' : 'butt'}
      lineJoin={obj.type === 'marker' ? 'round' : 'miter'}
      opacity={obj.type === 'marker' ? 0.5 : 1}
      rotation={obj.rotation || 0}
      draggable
      onClick={() => setSelectedId(obj.id)}
      onTap={() => setSelectedId(obj.id)}
      onDragEnd={(e) => handleDragEnd(obj.id, e)}
      onTransformEnd={(e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();

        // スケールをリセットし、pointsにスケールを反映
        node.scaleX(1);
        node.scaleY(1);

        const newPoints = (obj.points || []).map((p, i) =>
          i % 2 === 0 ? p * scaleX : p * scaleY
        );

        setObjects(objects.map(o => {
          if (o.id === obj.id) {
            return {
              ...o,
              x: node.x(),
              y: node.y(),
              points: newPoints,
              rotation: node.rotation(),
            };
          }
          return o;
        }));
      }}
    />
  );
};

/**
 * 画像オブジェクトのレンダリング
 */
export const renderImageObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode | null => {
  const { setSelectedId, setObjects, handleDragEnd, objects, loadedImages, refreshTransformer } = handlers;

  if (!loadedImages[obj.id]) {
    return null;
  }

  return (
    <KonvaImage
      key={obj.id}
      id={obj.id}
      image={loadedImages[obj.id]}
      x={obj.x}
      y={obj.y}
      width={obj.width}
      height={obj.height}
      rotation={obj.rotation || 0}
      draggable
      onClick={() => setSelectedId(obj.id)}
      onTap={() => setSelectedId(obj.id)}
      onDragEnd={(e) => handleDragEnd(obj.id, e)}
      onTransformEnd={(e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const newWidth = Math.max(10, (obj.width || 0) * scaleX);
        const newHeight = Math.max(10, (obj.height || 0) * scaleY);

        // スケールをリセットしてサイズを直接設定
        node.scaleX(1);
        node.scaleY(1);
        node.width(newWidth);
        node.height(newHeight);

        setObjects(objects.map(o => {
          if (o.id === obj.id) {
            return {
              ...o,
              x: node.x(),
              y: node.y(),
              width: newWidth,
              height: newHeight,
              rotation: node.rotation(),
            };
          }
          return o;
        }));
        // Transformerを更新
        setTimeout(() => refreshTransformer?.(), 0);
      }}
    />
  );
};

/**
 * オブジェクトをタイプに応じてレンダリング
 */
export const renderEditorObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode | null => {
  switch (obj.type) {
    case 'text':
      return renderTextObject(obj, handlers);
    case 'rect':
      return renderRectObject(obj, handlers);
    case 'line':
    case 'marker':
      return renderLineObject(obj, handlers);
    case 'image':
      return renderImageObject(obj, handlers);
    default:
      return null;
  }
};

/**
 * 描画中のプレビューをレンダリング
 */
export const renderDrawingPreview = (props: DrawingPreviewProps): React.ReactNode | null => {
  const {
    isDrawing,
    currentPoints,
    currentTool,
    markerColor,
    markerStrokeWidth,
    rectFillEnabled,
    rectFillColor,
    rectStrokeEnabled,
    rectStrokeColor,
  } = props;

  if (!isDrawing || currentPoints.length < 2) {
    return null;
  }

  return (
    <>
      {currentTool === 'marker' && (
        <Line
          points={currentPoints}
          stroke={markerColor}
          strokeWidth={markerStrokeWidth}
          lineCap="round"
          lineJoin="round"
          opacity={0.5}
        />
      )}
      {currentTool === 'horizontal' && currentPoints.length >= 4 && (
        <Line
          points={[currentPoints[0], currentPoints[1], currentPoints[2], currentPoints[1]]}
          stroke={markerColor}
          strokeWidth={markerStrokeWidth}
        />
      )}
      {currentTool === 'rectangle' && currentPoints.length >= 4 && (
        <Rect
          x={Math.min(currentPoints[0], currentPoints[2])}
          y={Math.min(currentPoints[1], currentPoints[3])}
          width={Math.abs(currentPoints[2] - currentPoints[0])}
          height={Math.abs(currentPoints[3] - currentPoints[1])}
          fill={rectFillEnabled ? rectFillColor : undefined}
          stroke={rectStrokeEnabled ? rectStrokeColor : '#3b82f6'}
          strokeWidth={2}
          dash={rectStrokeEnabled ? undefined : [5, 5]}
          opacity={0.7}
        />
      )}
      {currentTool === 'text' && currentPoints.length >= 4 && (
        <Rect
          x={Math.min(currentPoints[0], currentPoints[2])}
          y={Math.min(currentPoints[1], currentPoints[3])}
          width={Math.abs(currentPoints[2] - currentPoints[0])}
          height={Math.abs(currentPoints[3] - currentPoints[1])}
          fill="transparent"
          stroke="#2563eb"
          strokeWidth={1}
          dash={[5, 5]}
          opacity={0.8}
        />
      )}
    </>
  );
};
